<template>
    <div class="chart-container">
        <div ref="chart" class="chart-instance"></div>
    </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
    props: {
        reportData: {
            type: Object,
            required: true,
        },
        healthSurveyTemplate: {
            type: Object,
            required: true,
        },
    },

    data: function () {
        return {
            // @see https://echarts.apache.org/en/option.html
            // @see https://echarts.apache.org/examples/en/editor.html?c=bar-y-category-stack&lang=js
            option: {
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow',
                    },
                },
                legend: {},
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '3%',
                    containLabel: true,
                },
                xAxis: {
                    type: 'value',
                    interval: 1,
                    min: 0,
                    max: 0,
                    // name: '人',
                },
                yAxis: {
                    type: 'category',
                    data: [''],
                },
                series: []
            },
            resizeTimer: null,
        }
    },

    computed: {
        /**
         * メンタルチェックの対象人数
         * @return {number}
         */
        usersCount: function () {
            return this.reportData.reports.length
        },

        /**
         * 重症度評価のラベルリスト
         * @return {object}
         */
        evaluationLabels: function () {
            let result = null

            // データ例: "evaluation_labels": [{ "1": ["問題なし", "要観察", "要相談"]}, ...]
            // @see https://localhost:8000/admin/api/health_survey_reports/1
            this.reportData.evaluation_labels.forEach((labels) => {
                for (let key in labels) {
                    if (key === this.healthSurveyTemplate.id.toString()) {
                        result = labels[key]
                    }
                }
            })
            return result
        },

        /**
         * 重症度評価の段階毎の件数を埋める
         * @return {object}
         */
        evaluations: function () {
            const dict = {}
            this.evaluationLabels.forEach((label) => dict[label] = 0)

            this.reportData.reports.forEach((report) => {
                // データ例: "evaluations": {"1": "問題なし", "2": "軽微", "3": "軽微"}
                // @see https://localhost:8000/admin/api/health_survey_reports/1
                const label = report.evaluations[this.healthSurveyTemplate.id]
                if (label) dict[label] += 1
            })
            return dict
        },

        /**
         * 棒グラフの背景色リスト
         * @return {string[]}
         */
        barColors: function () {
            if (this.evaluationLabels.length < 4) {
                return [
                    '#8CCDFF',
                    '#FFDE73',
                    '#FFBCC0',
                ]
            }
            else if (this.evaluationLabels.length < 5) {
                return [
                    '#8CCDFF',
                    '#B6EBAD',
                    '#FFDE73',
                    '#FFBCC0',
                ]
            }
            return [
                '#8CCDFF',
                '#A5E7F4',
                '#B6EBAD',
                '#FFDE73',
                '#FFBCC0',
            ]
        },

        /**
         * 棒グラフのラベル色リスト
         * @return {string[]}
         */
        colors: function () {
            if (this.evaluationLabels.length < 4) {
                return [
                    '#284f70',
                    '#836e2b',
                    '#8e3d40',
                ]
            }
            else if (this.evaluationLabels.length < 5) {
                return [
                    '#284f70',
                    '#3d7034',
                    '#836e2b',
                    '#8e3d40',
                ]
            }
            return [
                '#284f70',
                '#2c5d67',
                '#3d7034',
                '#836e2b',
                '#8e3d40',
            ]
        },
    },

    mounted: function () {
        this.initializeChart()
        this.drawChart()

        // 画面リサイズ時にグラフを再描画する
        window.addEventListener('resize', () => {
            clearTimeout(this.resizeTimer)
            this.resizeTimer = setTimeout(() => this.resizeChart(), 500)
        })
    },

    methods: {
        initializeChart: function () {
            const chartElement = this.$refs.chart
            if (!chartElement) return
            this.chart = echarts.init(chartElement)
        },

        resizeChart: function () {
            if (!this.chart) return
            this.chart.resize()
        },

        drawChart: function () {
            if (!this.chart) return

            this.option.series = this.evaluationLabels.map((label, index) => {
                const data = this.evaluations[label]

                return {
                    name: label,
                    type: 'bar',
                    stack: 'total',
                    label: {
                        show: data > 0,
                        color: this.colors[index],
                        fontSize: '12px',
                    },
                    emphasis: {
                        focus: 'series',
                    },
                    color: this.barColors[index],
                    data: [data],
                }
            })
            this.option.xAxis.max = this.usersCount
            this.option.tooltip = {
                trigger: 'item',
                renderMode: 'html',
                formatter: '{a}: {c}人',
                position: 'top',
            }

            this.chart.setOption(this.option)
        },
    },
}
</script>

<style scoped lang="scss">
.chart-container {
    width: 100%;
    height: 180px;
}
.chart-instance {
    width: 100%;
    height: 100%;
}
</style>
